import {
  getExamEventDetailsData,
  useExamEventDetails,
  useSessionManagement,
} from '@exam-center/exam-event-management';
import {
  ExamType,
  FilterOperator,
  GetExamEventByIdDocument,
  OrderType,
  RegistrationStatus,
  useSearchExamCenterCoursesQuery,
  useUpdateExamEventMutation,
} from '@exam-center/graphql';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loading } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { ExamEventDetailsStatusForm } from '../../components';
import { useExamEventDetailsEditModal } from '../../providers';
import { getSnackbarMessageByNewStatus } from '../../utils';

import { type ExamEventDetailsStatusFormType, ExamEventDetailsStatusSchema } from './schema';

export default function ExamEventDetailsStatusLogic() {
  const { examEvent } = useExamEventDetails();
  const { showPromiseSnackbar } = useSnackbar();
  const { setAction } = useExamEventDetailsEditModal();

  const [updateExamEvent] = useUpdateExamEventMutation({
    refetchQueries: [
      {
        query: GetExamEventByIdDocument,
        variables: {
          getExamEventByIdInput: {
            id: examEvent.id,
          },
        },
      },
    ],
  });

  const { data: examCenterCoursesData, loading: isLoadingExamCenterCourses } =
    useSearchExamCenterCoursesQuery({
      variables: {
        searchExamCenterCoursesInput: {
          filter: {
            conditions: [
              {
                field: 'examCenterCode',
                operator: FilterOperator.Equal,
                value: examEvent.examCenterCode,
              },
            ],
          },
          paging: {
            currentPage: 0,
            pageSize: 200,
          },
          order: {
            type: OrderType.Asc,
            field: 'examId',
          },
        },
      },
      fetchPolicy: 'network-only',
    });

  const isAllExamCoursesSelected =
    examEvent.courses?.length === examCenterCoursesData?.ecSearchExamCenterCourses.total;

  const getDefaultValues = useCallback((): ExamEventDetailsStatusFormType => {
    return {
      status: examEvent.registrationStatus,
    };
  }, [examEvent]);

  const formContext = useForm<ExamEventDetailsStatusFormType>({
    defaultValues: async () => {
      return getDefaultValues();
    },
    resolver: zodResolver(ExamEventDetailsStatusSchema),
  });

  const handleUpdateExamEvent = useCallback(
    async (
      data: ExamEventDetailsStatusFormType & {
        reason?: string;
      },
    ) => {
      await updateExamEvent({
        variables: {
          updateExamEventInput: {
            id: examEvent.id,
            toRegistrationStatus: {
              status: data.status,
              reason: data.reason,
            },
          },
        },
      });
    },
    [examEvent, updateExamEvent],
  );

  const handleShowPromiseSnackbar = useCallback(
    (
      data: ExamEventDetailsStatusFormType & {
        reason?: string;
      },
    ) => {
      const message = getSnackbarMessageByNewStatus({
        from: examEvent.registrationStatus,
        to: data.status,
      });

      return showPromiseSnackbar({
        message,
        promise: () => handleUpdateExamEvent(data),
      });
    },
    [examEvent, showPromiseSnackbar, handleUpdateExamEvent],
  );

  const handleSubmit = async (data: ExamEventDetailsStatusFormType) => {
    if (data.status === RegistrationStatus.Canceled) {
      setAction({
        action: 'cancel',
        data: {
          onSubmit: (reason) => {
            handleShowPromiseSnackbar({
              ...data,
              reason,
            });
          },
        },
      });
    } else if (
      data.status === RegistrationStatus.TemporarilyClosed &&
      data.status !== examEvent.registrationStatus
    ) {
      setAction({
        action: 'temporarily-close',
        data: {
          onSubmit: (reason) => {
            handleShowPromiseSnackbar({
              ...data,
              reason,
            });
          },
        },
      });
    } else {
      handleShowPromiseSnackbar(data);
    }
  };

  const { examType, registrationType, room, registrationStatusOptions } =
    getExamEventDetailsData(examEvent);

  const { sessions, totalDuration } = useSessionManagement(examEvent.sessions);

  if (formContext.formState.isLoading || isLoadingExamCenterCourses) {
    return (
      <Loading
        sx={{
          height: '100dvh',
        }}
      />
    );
  }

  return (
    <ExamEventDetailsStatusForm
      form={formContext}
      onSubmit={handleSubmit}
      data={{
        basicInformation: {
          examEventName: examEvent.name,
          examType: examType,
          registrationType: registrationType,
        },
        examSchedule: {
          examDate: {
            examStartedAt: examEvent.examStartedAt,
            examEndedAt: examEvent.examEndedAt,
            eventStartDate: examEvent.registrationStartedAt,
            eventEndDate: examEvent.registrationEndedAt,
          },
          sessions: {
            data: examEvent.examType === ExamType.Computer ? sessions : [],
            totalDuration,
          },
        },
        examCourses: {
          isAllSelected: isAllExamCoursesSelected,
          courses: examEvent.courses
            .map((course) => course.examCourse?.name ?? null)
            .filter((name) => name !== null),
        },
        location: {
          province: {
            province: examEvent.examLocation.province.nameTh,
            location: examEvent.examLocation.nameTh,
          },
          room: {
            roomData: room
              ? room.map((room) => ({
                  ...room,
                  name: room.name,
                }))
              : [],
          },
          numberOfExaminees: examEvent.numberOfExaminees ?? 0,
          seat: examEvent.seat,
        },
        optionalInformation: {
          applicantGroup: {
            code: examEvent.metadata?.applicantGroup?.code,
            type: examEvent.metadata?.applicantGroup?.type,
          },
        },
        examEventStatus: {
          registrationStatusOptions,
        },
      }}
    />
  );
}
